/**
 * useCustomerTable Hook
 * Table-specific state and logic for customer list including sorting
 */

import { useState, useCallback } from 'react';
import type { SortState, SortableColumn } from '../types/customerTable.types';
import { calculateNextDirection, mapColumnToApiField } from '../utils/sortUtils';

export interface UseCustomerTableReturn {
  // Menu state
  anchorEl: HTMLElement | null;
  menuCustomerId: string | null;
  handleMenuOpen: (event: React.MouseEvent<HTMLElement>, customerId: string) => void;
  handleMenuClose: () => void;

  // Pagination handlers
  handlePrevious: () => void;
  handleNext: () => void;

  // Sort state and handlers
  sortState: SortState;
  handleSort: (columnId: SortableColumn) => void;
}

interface UseCustomerTableOptions {
  page: number;
  totalPages: number;
  setPage: (page: number) => void;
  setSort: (field: string, order: 'asc' | 'desc') => void;
}

export function useCustomerTable(options: UseCustomerTableOptions): UseCustomerTableReturn {
  const { page, totalPages, setPage, setSort } = options;
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [menuCustomerId, setMenuCustomerId] = useState<string | null>(null);

  // Default sort: "2 Weeks" descending (highest at top)
  const [sortState, setSortState] = useState<SortState>({
    column: 'dueWithin2Weeks',
    direction: 'desc',
  });

  // Menu handlers
  const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, customerId: string) => {
    setAnchorEl(event.currentTarget);
    setMenuCustomerId(customerId);
  }, []);

  const handleMenuClose = useCallback(() => {
    setAnchorEl(null);
    setMenuCustomerId(null);
  }, []);

  // Pagination handlers
  const handlePrevious = useCallback(() => {
    if (page > 1) {
      setPage(page - 1);
    }
  }, [page, setPage]);

  const handleNext = useCallback(() => {
    if (page < totalPages) {
      setPage(page + 1);
    }
  }, [page, totalPages, setPage]);

  // Sort handler
  const handleSort = useCallback((columnId: SortableColumn) => {
    // console.log('[SORT DEBUG] handleSort called for column:', columnId);
    // console.log('[SORT DEBUG] Current sort state:', sortState);

    const newDirection = calculateNextDirection(
      sortState.column,
      sortState.direction,
      columnId
    );

    // console.log('[SORT DEBUG] New direction:', newDirection);

    const newSortState: SortState = {
      column: newDirection ? columnId : null,
      direction: newDirection,
    };

    setSortState(newSortState);

    // Update API sort parameters
    if (newDirection) {
      const apiField = mapColumnToApiField(columnId);
      setSort(apiField, newDirection);
    } else {
      // Clear sort - reset to default (trucks_due_within_2_weeks desc)
      setSort('trucks_due_within_2_weeks', 'desc');
    }
  }, [sortState, setSort]);

  return {
    anchorEl,
    menuCustomerId,
    handleMenuOpen,
    handleMenuClose,
    handlePrevious,
    handleNext,
    sortState,
    handleSort,
  };
}
